import React, { useState } from 'react';
import {
  VARIANT_FIELDS, COMMON_SPEC_FIELDS,
  BADGE_GROUPS, badgeSrc, badgeExists,
} from '../badges';
import { detailEntries } from '../datasheet';
import { useRegion } from '../region';
import USEditorView from '../usds/editor/USEditorView';
import { ImageUpload } from './ImageUpload';
import InternalNote from './InternalNote';

const fileToDataUrl = (file, cb) => { if (!file) return; const r = new FileReader(); r.onload = () => cb(r.result); r.readAsDataURL(file); };
const uid = () => 'f' + Math.random().toString(36).slice(2, 8);

export default function EditorView(props) {
  const region = useRegion();
  if (region === 'US') return <USEditorView {...props} />;
  return <AUEditorView {...props} />;
}

function AUEditorView({ sheet, setSheet }) {
  const set = (patch) => setSheet(s => ({ ...s, ...patch }));
  const setDetail = (id, v) => setSheet(s => ({ ...s, productDetails: { ...s.productDetails, [id]: v } }));
  const setSpec = (id, v) => setSheet(s => ({ ...s, commonSpecs: { ...s.commonSpecs, [id]: v } }));
  const onImage = (field, file) => fileToDataUrl(file, url => setSheet(s => ({ ...s, [field]: url })));

  // ---- custom rows (product details / common specs) ----
  const addCustom = (key) => setSheet(s => ({ ...s, [key]: [...(s[key] || []), { label: '', value: '' }] }));
  const setCustom = (key, i, k, v) => setSheet(s => ({ ...s, [key]: (s[key] || []).map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const delCustom = (key, i) => setSheet(s => ({ ...s, [key]: (s[key] || []).filter((_, idx) => idx !== i) }));

  // ---- product detail ordering ----
  const moveDetail = (key, dir) => setSheet(s => {
    const keys = detailEntries(s).map(e => e.key);
    const i = keys.indexOf(key); const j = i + dir;
    if (i < 0 || j < 0 || j >= keys.length) return s;
    [keys[i], keys[j]] = [keys[j], keys[i]];
    return { ...s, detailOrder: keys };
  });
  const delCustomDetail = (i) => setSheet(s => {
    const rows = (s.productDetailsCustom || []).filter((_, idx) => idx !== i);
    const order = (s.detailOrder || []).filter(k => k !== 'custom:' + i).map(k => {
      if (k.indexOf('custom:') === 0) { const n = +k.slice(7); return n > i ? 'custom:' + (n - 1) : k; }
      return k;
    });
    return { ...s, productDetailsCustom: rows, detailOrder: order };
  });

  // ---- variants + custom variant columns ----
  const cols = sheet.variantColumns || [];
  const addVariant = () => setSheet(s => ({ ...s, variants: [...(s.variants || []), {}] }));
  const setVariant = (i, id, v) => setSheet(s => ({ ...s, variants: (s.variants || []).map((row, idx) => idx === i ? { ...row, [id]: v } : row) }));
  const removeVariant = (i) => setSheet(s => ({ ...s, variants: (s.variants || []).filter((_, idx) => idx !== i) }));
  const addVarCol = () => setSheet(s => ({ ...s, variantColumns: [...(s.variantColumns || []), { id: uid(), label: 'New Column' }] }));
  const renameVarCol = (id, label) => setSheet(s => ({ ...s, variantColumns: (s.variantColumns || []).map(c => c.id === id ? { ...c, label } : c) }));
  const delVarCol = (id) => setSheet(s => ({ ...s, variantColumns: (s.variantColumns || []).filter(c => c.id !== id) }));

  // ---- System Lumen / Beam Angle columns (per-variant lumen under each beam) ----
  const beamCols = sheet.beamCols || [];
  const addBeamCol = () => setSheet(s => ({ ...s, beamCols: [...(s.beamCols || []), { name: '', angle: '' }] }));
  const setBeamAngle = (i, angle) => setSheet(s => ({ ...s, beamCols: (s.beamCols || []).map((c, idx) => idx === i ? { ...c, angle } : c) }));
  const renameBeamCol = (i, name) => setSheet(s => {
    const old = ((s.beamCols || [])[i] || {}).name;
    return {
      ...s,
      beamCols: (s.beamCols || []).map((c, idx) => idx === i ? { ...c, name } : c),
      variants: (s.variants || []).map(v => {
        if (!v.beamLumens || !(old in v.beamLumens)) return v;
        const bl = { ...v.beamLumens }; bl[name] = bl[old]; delete bl[old];
        return { ...v, beamLumens: bl };
      }),
    };
  });
  const delBeamCol = (i) => setSheet(s => {
    const name = ((s.beamCols || [])[i] || {}).name;
    return {
      ...s,
      beamCols: (s.beamCols || []).filter((_, idx) => idx !== i),
      variants: (s.variants || []).map(v => {
        if (!v.beamLumens) return v;
        const bl = { ...v.beamLumens }; delete bl[name];
        return { ...v, beamLumens: bl };
      }),
    };
  });
  const setBeamLumen = (i, name, val) => setSheet(s => ({
    ...s,
    variants: (s.variants || []).map((row, idx) => idx === i ? { ...row, beamLumens: { ...(row.beamLumens || {}), [name]: val } } : row),
  }));

  const toggleBadge = (key) => setSheet(s => ({ ...s, badges: { ...(s.badges || {}), [key]: !((s.badges || {})[key]) } }));

  // ---- page 2 list helpers ----
  const addItem = (key, blank) => setSheet(s => ({ ...s, [key]: [...(s[key] || []), blank] }));
  const setItem = (key, i, k, v) => setSheet(s => ({ ...s, [key]: (s[key] || []).map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const delItem = (key, i) => setSheet(s => ({ ...s, [key]: (s[key] || []).filter((_, idx) => idx !== i) }));
  const setItemImg = (key, i, file) => fileToDataUrl(file, url => setItem(key, i, 'image', url));

  const badgesSection = (
    <Section title="Badges" testid="section-badges" defaultOpen={false}>
      {Object.entries(BADGE_GROUPS).map(([group, keys]) => (
        <div className="badge-group" key={group}>
          <div className="badge-group-title">{group}</div>
          {group === 'Accessory Options' && <p className="field-hint" style={{ margin: '0 0 6px' }}>These appear in the Accessories section of the datasheet (page 2), not the badge strip.</p>}
          <div className="badge-grid">
            {keys.filter(badgeExists).map(key => {
              const on = !!((sheet.badges || {})[key]);
              return (
                <button type="button" key={key} className={'badge-toggle' + (on ? ' on' : '')} data-testid={'badge-' + key} onClick={() => toggleBadge(key)} title={key}>
                  <img src={badgeSrc(key)} alt={key} /><span>{key}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </Section>
  );

  // Housing datasheets only use: name/code/corner/family, images, spec sections, badges.
  if ((sheet.usType || 'product') === 'housing') {
    return (
      <div className="editor-form" data-testid="editor-form">
        <InternalNote sheet={sheet} setSheet={setSheet} />
        <Section title="Product" testid="section-product">
          <Field label="Product Name"><input className="input" data-testid="input-name" value={sheet.name} onChange={e => set({ name: e.target.value })} placeholder="e.g. POCO M ROUND HOUSING" /></Field>
          <Field label="Model Code" hint="(H-… code drives the fixed matrix columns)"><input className="input" data-testid="input-code" value={sheet.code} onChange={e => set({ code: e.target.value })} placeholder="e.g. H-R40-IHBW1-15-D2-UNV-NR" /></Field>
          <Field label="Corner Note" hint="(optional, top-right box)"><input className="input" data-testid="input-corner" value={sheet.corner} onChange={e => set({ corner: e.target.value })} placeholder="e.g. project ref, drawing no." /></Field>
          <Field label="Family" hint="(groups this item in Core Range)"><input className="input" data-testid="input-family" value={sheet.family || ''} onChange={e => set({ family: e.target.value })} placeholder="e.g. Poco" /></Field>
        </Section>

        <Section title="Images" testid="section-images">
          <div className="grid2">
            <ImageSlot label="Housing / Frame Drawings" url={sheet.productImage} onFile={f => onImage('productImage', f)} onClear={() => set({ productImage: '' })} testid="img-product" />
            <ImageSlot label="Dimensions Drawing (optional)" url={sheet.dimensionsImage} onFile={f => onImage('dimensionsImage', f)} onClear={() => set({ dimensionsImage: '' })} testid="img-dimensions" />
          </div>
        </Section>

        <Section title="Specification Sections" testid="section-housing-specs">
          <p className="field-hint" style={{ margin: '0 0 8px' }}>Each section shows as a titled bullet list on the sheet (e.g. Electrical, Mounting, Labels) — one bullet per line.</p>
          {(sheet.commonSpecsCustom || []).map((r, i) => (
            <div className="housing-spec-row" key={i} data-testid={'housing-spec-' + i}>
              <div className="housing-spec-head">
                <input className="input" placeholder="Section title (e.g. Electrical)" data-testid={'housing-spec-label-' + i} value={r.label || ''} onChange={e => setCustom('commonSpecsCustom', i, 'label', e.target.value)} />
                <button className="btn btn-sm btn-danger" data-testid={'housing-spec-del-' + i} onClick={() => delCustom('commonSpecsCustom', i)} title="Remove section">✕</button>
              </div>
              <textarea className="textarea" rows={4} placeholder={'One bullet per line, e.g.\nField-replaceable LED driver\nInternal thermal protection'} data-testid={'housing-spec-value-' + i} value={r.value || ''} onChange={e => setCustom('commonSpecsCustom', i, 'value', e.target.value)} />
            </div>
          ))}
          <button className="btn btn-sm" data-testid="housing-spec-add" onClick={() => addCustom('commonSpecsCustom')}>＋ Add section</button>
        </Section>

        {badgesSection}
      </div>
    );
  }

  return (
    <div className="editor-form" data-testid="editor-form">
      <InternalNote sheet={sheet} setSheet={setSheet} />
      <Section title="Product" testid="section-product">
        <Field label="Product Name"><input className="input" data-testid="input-name" value={sheet.name} onChange={e => set({ name: e.target.value })} placeholder="e.g. HADIS" /></Field>
        <Field label="Variant Tag" hint="(shown in parentheses)"><input className="input" data-testid="input-variant" value={sheet.variant} onChange={e => set({ variant: e.target.value })} placeholder="e.g. SUSPENDED FIXED MEDIUM" /></Field>
        <Field label="Model Code"><input className="input" data-testid="input-code" value={sheet.code} onChange={e => set({ code: e.target.value })} placeholder="e.g. DP-HADM" /></Field>
        <Field label="Corner Note" hint="(optional, top-right box)"><input className="input" data-testid="input-corner" value={sheet.corner} onChange={e => set({ corner: e.target.value })} placeholder="e.g. project ref, drawing no." /></Field>
        <Field label="Family" hint="(groups this item in Core Range)"><input className="input" data-testid="input-family" value={sheet.family || ''} onChange={e => set({ family: e.target.value })} placeholder="e.g. Parker" /></Field>
      </Section>

      <Section title="Images" testid="section-images">
        <div className="grid2">
          <ImageSlot label="Product Image" url={sheet.productImage} onFile={f => onImage('productImage', f)} onClear={() => set({ productImage: '' })} testid="img-product" />
          <ImageSlot label="Dimensions Drawing" url={sheet.dimensionsImage} onFile={f => onImage('dimensionsImage', f)} onClear={() => set({ dimensionsImage: '' })} testid="img-dimensions" />
          <ImageSlot label="Extra Image (optional)" url={sheet.extraImage} onFile={f => onImage('extraImage', f)} onClear={() => set({ extraImage: '' })} testid="img-extra" />
        </div>
        <p className="field-hint" style={{ margin: '8px 0 0' }}>The extra image sits beside the badges on page 1 — use it for a second product angle or a mounting detail instead of squeezing two pictures into one slot.</p>
      </Section>

      <Section title="Product Details" testid="section-details">
        <p className="field-hint" style={{ margin: '0 0 8px' }}>Use ↑ ↓ to change the order fields appear on the datasheet. Empty fields are hidden automatically.</p>
        {detailEntries(sheet).map((e, idx, arr) => (
          <div className="detail-row" key={e.key}>
            {e.type === 'preset' ? (
              <Field label={e.field.label}><input className="input" data-testid={'detail-' + e.field.id} value={(sheet.productDetails || {})[e.field.id] || ''} onChange={ev => setDetail(e.field.id, ev.target.value)} placeholder={e.field.placeholder} /></Field>
            ) : (
              <div className="field">
                <label className="field-label">Custom detail</label>
                <div className="custom-row" style={{ marginBottom: 0 }}>
                  <input className="input" placeholder="Field label" data-testid={'detail-custom-label-' + e.index} value={e.row.label || ''} onChange={ev => setCustom('productDetailsCustom', e.index, 'label', ev.target.value)} />
                  <input className="input" placeholder="Value" data-testid={'detail-custom-value-' + e.index} value={e.row.value || ''} onChange={ev => setCustom('productDetailsCustom', e.index, 'value', ev.target.value)} />
                  <button className="btn btn-sm btn-danger" onClick={() => delCustomDetail(e.index)} title="Remove">✕</button>
                </div>
              </div>
            )}
            <div className="reorder-btns">
              <button className="btn btn-sm" data-testid={'detail-up-' + e.key} disabled={idx === 0} onClick={() => moveDetail(e.key, -1)} title="Move up">↑</button>
              <button className="btn btn-sm" data-testid={'detail-down-' + e.key} disabled={idx === arr.length - 1} onClick={() => moveDetail(e.key, 1)} title="Move down">↓</button>
            </div>
          </div>
        ))}
        <button className="btn btn-sm" data-testid="detail-add-custom" onClick={() => addCustom('productDetailsCustom')}>＋ Add custom detail</button>
      </Section>

      <Section title="Variant Table" testid="section-variants">
        <p className="field-hint" style={{ margin: '0 0 8px' }}>Columns with no values are automatically hidden on the datasheet &amp; PDF.</p>
        {beamCols.length > 0 && (
          <div className="beam-cols-block" data-testid="beam-cols-block">
            <div className="beam-cols-title">System Lumen (lm) / Beam Angle columns</div>
            {beamCols.map((c, i) => (
              <div className="beam-col-row" key={i} data-testid={'beam-col-' + i}>
                <input className="input" data-testid={'beam-col-name-' + i} placeholder="Column name (e.g. Narrow or 20°)" value={c.name || ''} onChange={e => renameBeamCol(i, e.target.value)} />
                <input className="input" data-testid={'beam-col-angle-' + i} placeholder="Angles shown under name (e.g. 15°/11°/11°/15°)" value={c.angle || ''} onChange={e => setBeamAngle(i, e.target.value)} />
                <button className="btn btn-sm btn-danger" data-testid={'beam-col-del-' + i} onClick={() => delBeamCol(i)} title="Remove beam column">✕</button>
              </div>
            ))}
            <p className="field-hint" style={{ margin: '4px 0 0' }}>Each variant row below gets a lumen box per beam column — they render grouped under “System Lumen (lm) / Beam Angle” on the sheet.</p>
          </div>
        )}
        {(sheet.variants || []).length > 0 && (
          <div className="variant-head">
            {VARIANT_FIELDS.map(c => <span key={c.id} className="vh">{c.label}</span>)}
            {cols.map(c => (
              <span key={c.id} className="vh vh-custom">
                <input className="vh-input" data-testid={'varcol-' + c.id} value={c.label} onChange={e => renameVarCol(c.id, e.target.value)} />
                <button className="vh-del" onClick={() => delVarCol(c.id)} title="Remove column">✕</button>
              </span>
            ))}
            {beamCols.map((c, i) => <span key={'b' + i} className="vh vh-beam">{(c.name || 'Beam') + ' (lm)'}</span>)}
            <span className="vh" />
          </div>
        )}
        {(sheet.variants || []).map((row, i) => (
          <div className="variant-row" key={i}>
            {VARIANT_FIELDS.map(c => <input key={c.id} className="input" data-testid={`variant-${i}-${c.id}`} value={row[c.id] || ''} placeholder={c.label} onChange={e => setVariant(i, c.id, e.target.value)} />)}
            {cols.map(c => <input key={c.id} className="input" data-testid={`variant-${i}-${c.id}`} value={row[c.id] || ''} placeholder={c.label} onChange={e => setVariant(i, c.id, e.target.value)} />)}
            {beamCols.map((c, j) => (
              <input key={'b' + j} className="input input-beam" data-testid={`variant-${i}-beam-${j}`}
                value={(row.beamLumens || {})[c.name] || ''} placeholder={(c.name || 'Beam') + ' lm'}
                onChange={e => setBeamLumen(i, c.name, e.target.value)} />
            ))}
            <button className="btn btn-sm btn-danger" data-testid={`variant-remove-${i}`} onClick={() => removeVariant(i)} title="Remove">✕</button>
          </div>
        ))}
        <div className="row-actions">
          <button className="btn btn-sm" data-testid="variant-add" onClick={addVariant}>＋ Add Variant Row</button>
          <button className="btn btn-sm" data-testid="variant-add-col" onClick={addVarCol}>＋ Add Custom Column</button>
          <button className="btn btn-sm" data-testid="variant-add-beam" onClick={addBeamCol}>＋ Add Beam Angle Column</button>
        </div>
      </Section>

      <Section title="Common Specifications" testid="section-specs">
        <div className="grid2">
          {COMMON_SPEC_FIELDS.map(f => (
            <Field key={f.id} label={f.label}><textarea className="textarea" data-testid={'spec-' + f.id} value={(sheet.commonSpecs || {})[f.id] || ''} onChange={e => setSpec(f.id, e.target.value)} placeholder={(f.placeholder || '').replace(/\\n/g, '\n')} /></Field>
          ))}
        </div>
        <CustomRows rows={sheet.commonSpecsCustom} testid="spec" label="custom specification"
          onAdd={() => addCustom('commonSpecsCustom')} onSet={(i, k, v) => setCustom('commonSpecsCustom', i, k, v)} onDel={i => delCustom('commonSpecsCustom', i)} />
      </Section>

      {badgesSection}

      <Section title="Page 2 · Photometrics" testid="section-photometrics" defaultOpen={false}>
        <p className="field-hint" style={{ margin: '0 0 8px' }}>Diagrams lay out left-to-right, 3 per row — up to 6 fit on the sheet (they shrink automatically when accessories/bezels are present).</p>
        <Field label="Subtitle"><input className="input" data-testid="photo-subtitle" value={sheet.photoSubtitle || ''} onChange={e => set({ photoSubtitle: e.target.value })} placeholder="e.g. standard beam angles, alternative available on request" /></Field>
        {(sheet.photometrics || []).map((p, i) => (
          <div className="page2-item" key={i} data-testid={'photo-' + i}>
            <input className="input" placeholder="Label (e.g. 25° Polar Diagram)" value={p.label || ''} onChange={e => setItem('photometrics', i, 'label', e.target.value)} />
            <ImageInline url={p.image} onFile={f => setItemImg('photometrics', i, f)} onClear={() => setItem('photometrics', i, 'image', '')} testid={'photo-img-' + i} />
            <button className="btn btn-sm btn-danger" onClick={() => delItem('photometrics', i)}>✕</button>
          </div>
        ))}
        <button className="btn btn-sm" data-testid="photo-add" onClick={() => addItem('photometrics', { label: '', image: '' })}>＋ Add Photometric</button>
      </Section>

      <Section title="Page 2 · Bezel Options" testid="section-bezel" defaultOpen={false}>
        {(sheet.bezelOptions || []).map((b, i) => (
          <div className="page2-item" key={i} data-testid={'bezel-' + i}>
            <input className="input" placeholder="Name (e.g. White)" value={b.name || ''} onChange={e => setItem('bezelOptions', i, 'name', e.target.value)} />
            <ImageInline url={b.image} onFile={f => setItemImg('bezelOptions', i, f)} onClear={() => setItem('bezelOptions', i, 'image', '')} testid={'bezel-img-' + i} />
            <button className="btn btn-sm btn-danger" onClick={() => delItem('bezelOptions', i)}>✕</button>
          </div>
        ))}
        <button className="btn btn-sm" data-testid="bezel-add" onClick={() => addItem('bezelOptions', { name: '', image: '' })}>＋ Add Bezel Option</button>
      </Section>

      <Section title="Page 2 · Accessories" testid="section-accessories" defaultOpen={false}>
        {(sheet.accessories || []).map((a, i) => (
          <div className="page2-item" key={i} data-testid={'acc-' + i}>
            <input className="input" placeholder="Name" value={a.name || ''} onChange={e => setItem('accessories', i, 'name', e.target.value)} />
            <input className="input" placeholder="Code" value={a.code || ''} onChange={e => setItem('accessories', i, 'code', e.target.value)} />
            <ImageInline url={a.image} onFile={f => setItemImg('accessories', i, f)} onClear={() => setItem('accessories', i, 'image', '')} testid={'acc-img-' + i} />
            <button className="btn btn-sm btn-danger" onClick={() => delItem('accessories', i)}>✕</button>
          </div>
        ))}
        <button className="btn btn-sm" data-testid="acc-add" onClick={() => addItem('accessories', { name: '', code: '', image: '' })}>＋ Add Accessory</button>
      </Section>
    </div>
  );
}

function CustomRows({ rows, onAdd, onSet, onDel, testid, label }) {
  return (
    <div className="custom-rows">
      {(rows || []).map((r, i) => (
        <div className="custom-row" key={i} data-testid={`${testid}-custom-${i}`}>
          <input className="input" placeholder="Field label" value={r.label || ''} onChange={e => onSet(i, 'label', e.target.value)} />
          <input className="input" placeholder="Value" value={r.value || ''} onChange={e => onSet(i, 'value', e.target.value)} />
          <button className="btn btn-sm btn-danger" onClick={() => onDel(i)} title="Remove">✕</button>
        </div>
      ))}
      <button className="btn btn-sm" data-testid={`${testid}-add-custom`} onClick={onAdd}>＋ Add {label}</button>
    </div>
  );
}

function Section({ title, children, testid, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={'form-section' + (open ? '' : ' collapsed')} data-testid={testid}>
      <button type="button" className="section-head" onClick={() => setOpen(o => !o)}><span className={'caret' + (open ? ' open' : '')}>▸</span>{title}</button>
      {open && <div className="section-body">{children}</div>}
    </div>
  );
}

function Field({ label, hint, children }) {
  return <div className="field"><label className="field-label">{label}{hint && <span className="field-hint"> {hint}</span>}</label>{children}</div>;
}

function ImageSlot({ label, url, onFile, onClear, testid }) {
  return (
    <div className="field" data-testid={testid}>
      <label className="field-label">{label}</label>
      <ImageUpload url={url} onFile={onFile} onClear={onClear} testid={testid} />
    </div>
  );
}

function ImageInline({ url, onFile, onClear, testid }) {
  return <ImageUpload url={url} onFile={onFile} onClear={onClear} testid={testid} inline />;
}
